// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Periods } from '@shared/utils/History/TFInfo';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { SolidBrush } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { TerceraChartMainPriceRenderer } from './TerceraChartMainPriceRenderer';
import { TerceraChartDrawingType } from '../Utils/ChartConstants';
import { BaseInterval } from '@shared/utils/History/BaseInterval';
import { LayersEnum } from '../Utils/LayersEnum';
export class GoToTabletRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.timeOutInSeconds = 2000;
        this.assignLayer = LayersEnum.Tools;
        this.SetClassName('GoToTabletRenderer');
    }
    Localize() {
        super.Localize();
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.tabletBrush = new SolidBrush(ThemeManager.colors.GoToTabletColor);
        this.textBrush = new SolidBrush(ThemeManager.colors.GoToTabletFontColor);
        this.textFont = ThemeManager.Fonts.Font_12F_regular;
    }
    Dispose() {
        clearTimeout(this.goToTabletTimer);
        super.Dispose();
    }
    Draw(gr, window, windowsContainer, advParams) {
        if (isNullOrUndefined(this.date)) {
            return;
        }
        const TABLET_HEIGHT = 34;
        const ARROW_WIDTH = 17;
        const ARROW_HEIGHT = 7;
        const TEXT_PADDING = 8;
        const TABLET_WIDTH = gr.GetTextWidth(this.dateStr, this.textFont) + TEXT_PADDING * 2;
        const clientRect = window.ClientRectangle;
        const barWidth = TerceraChartMainPriceRenderer.ProcessBarWidth(window);
        const selectedBar = this.getSelectedBar();
        if (isNullOrUndefined(selectedBar))
            return;
        gr.save();
        gr.beginPath();
        gr.rect(clientRect.X, clientRect.Y, clientRect.Width, clientRect.Height);
        gr.clip();
        const barX = Math.ceil(window.PointsConverter.GetScreenXbyTime(this.date.getTime()));
        const x = Math.floor(barX + (barWidth.leftBorder + barWidth.barW) / 2 - TABLET_WIDTH / 2);
        const startY = this.getStartY(window, advParams.mainPriceRenderer.ChartDrawingType, selectedBar);
        let y = Math.ceil(startY - TABLET_HEIGHT - ARROW_HEIGHT);
        let trianglePoints = [];
        const arrowX = x + TABLET_WIDTH / 2;
        let arrowY = 0;
        if (y < 0) {
            y = Math.ceil(startY + ARROW_HEIGHT);
            arrowY = y + 1;
            trianglePoints = [
                new Point(arrowX - ARROW_WIDTH / 2, arrowY),
                new Point(arrowX, arrowY - ARROW_HEIGHT),
                new Point(arrowX + ARROW_WIDTH / 2, arrowY)
            ];
        }
        else {
            arrowY = y + TABLET_HEIGHT - 1;
            trianglePoints = [
                new Point(arrowX - ARROW_WIDTH / 2, arrowY),
                new Point(arrowX, arrowY + ARROW_HEIGHT),
                new Point(arrowX + ARROW_WIDTH / 2, arrowY)
            ];
        }
        const tabletRect = new Rectangle(x, y, TABLET_WIDTH, TABLET_HEIGHT);
        gr.RoundRectangle(tabletRect, 4, this.tabletBrush);
        gr.FillPolygon(this.tabletBrush, trianglePoints);
        gr.DrawStringInRect(this.dateStr, this.textFont, this.textBrush, tabletRect, false, 'center', 'middle');
        gr.restore();
    }
    setDate(date) {
        this.date = date;
        this.dateStr = this.getDateStr(date);
        if (isNullOrUndefined(date)) {
            return;
        }
        this.goToTabletTimer = setTimeout(() => {
            this.setDate(null);
            clearTimeout(this.goToTabletTimer);
            this.chart.IsDirty(LayersEnum.Tools);
        }, this.timeOutInSeconds);
    }
    getDateStr(date) {
        if (isNullOrUndefined(date)) {
            return '';
        }
        const timeFrameInfo = this.chart.TimeFrameInfo();
        if (timeFrameInfo.Periods >= Periods.DAY) {
            return DateTimeUtils.formatDate(date, 'DD.MM.YYYY');
        }
        else {
            return DateTimeUtils.formatDate(date, 'DD.MM.YYYY HH:mm');
        }
    }
    getSelectedBar() {
        const cashItem = this.chart.MainCashItemSeries.CashItem;
        const barIndex = cashItem.FindIntervalByTime(this.date.getTime());
        return cashItem.GetInterval(barIndex);
    }
    getStartY(window, drawingType, bar) {
        var _a;
        let openPrice = bar.Data[BaseInterval.OPEN_INDEX];
        let closePrice = bar.Data[BaseInterval.CLOSE_INDEX];
        if ((_a = this.chart) === null || _a === void 0 ? void 0 : _a.cashItemSeriesSettings) {
            openPrice = this.chart.cashItemSeriesSettings.CalculateConverter(bar.Data[BaseInterval.OPEN_INDEX]);
            closePrice = this.chart.cashItemSeriesSettings.CalculateConverter(bar.Data[BaseInterval.CLOSE_INDEX]);
        }
        const fallenBar = openPrice > closePrice;
        switch (drawingType) {
            case TerceraChartDrawingType.Dot:
            case TerceraChartDrawingType.DotLine:
                return window.PointsConverter.GetScreenY(closePrice);
            default:
                return fallenBar
                    ? window.PointsConverter.GetScreenY(openPrice)
                    : window.PointsConverter.GetScreenY(closePrice);
        }
    }
}
